import { type MouseEvent } from 'react'
import { twMerge } from 'tailwind-merge'
import { useStoreContext } from '../contexts/store.context'
import { CategoryType } from '../types'

// Same as marketplace images
import categoryApe from '~/features/marketplace/assets/category-ape.png'
import categoryDroid from '~/features/marketplace/assets/category-droid.png'
import categoryWeapon from '~/features/marketplace/assets/category-weapon.png'
// Different images
import categoryLootbox from '~/features/store/assets/categories/loot-boxes.png'
import categoryFeatured from '~/features/store/assets/categories/featured.png'

const defaultCategoryImages = {
    featured: categoryFeatured,
    apes: categoryApe,
    droids: categoryDroid,
    weapons: categoryWeapon,
    'loot boxes': categoryLootbox,
}

/**
 * How many listings the category holds. The backend already annotates this per category (counting
 * only LISTED rows), but it was surfaced nowhere except the toolbar total on the far right of the
 * page — past the filters and the sort — which is the last place anyone looks.
 */
const CategoryCount = ({ count }: { count?: number }) => {
    if (typeof count !== 'number') return null
    return (
        <div className='absolute top-0 left-0 right-0 p-[0.75em] flex flex-row justify-end'>
            <span className='bg-black/75 backdrop-blur-sm border border-white/25 rounded-full px-[1em] py-[0.35em] text-[max(10px,0.65cqi)] leading-none tracking-wide whitespace-nowrap'>
                {count} {count === 1 ? 'ITEM' : 'ITEMS'}
            </span>
        </div>
    )
}

const CategoryCard = ({ category }: { category: CategoryType }) => {
    const { selectedCategory, selectCategory, searchTerm, setSearchTerm } = useStoreContext()
    const { name, count } = category

    const imgSrc = defaultCategoryImages[name.toLowerCase()]
    const isActive = selectedCategory === category && searchTerm === undefined

    const handleClick = (e: MouseEvent<HTMLDivElement>) => {
        e.currentTarget.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' })
        selectCategory(name)
        setSearchTerm(undefined)
    }

    return (
        <div
            className={twMerge(
                'group font-bold text-center flex flex-col text-[max(8px,0.5cqi)] min-w-56 flex-1 basis-0 p-[1em] gap-[1em] snap-center select-none cursor-pointer hover:bg-[linear-gradient(90deg,rgba(201,31,255,0.25)_3.2%,rgba(3,206,255,0.25)_97.74%)]',
                isActive
                    ? 'bg-[linear-gradient(90deg,rgba(201,31,255,0.25)_3.2%,rgba(3,206,255,0.25)_97.74%)]'
                    : 'bg-[#282828]'
            )}
            onClick={handleClick}
        >
            <div className='relative overflow-hidden' style={{ aspectRatio: '360/215' }}>
                {imgSrc && (
                    <img
                        src={imgSrc}
                        alt={name}
                        className='absolute inset-0 w-full h-full object-cover object-top'
                        draggable={false}
                    />
                )}
                <div className='absolute inset-0 bg-[linear-gradient(90deg,rgba(201,31,255,0.25)_3.2%,rgba(3,206,255,0.25)_97.74%)] opacity-0 group-hover:opacity-100 transition-opacity' />
                <CategoryCount count={count} />
                <div className='absolute bottom-0 left-0 right-0 p-3 uppercase bg-gradient-to-t from-black to-transparent'>
                    <div className='text-[2em]'>{name}</div>
                </div>
            </div>
            <div className='text-[1.5em] uppercase whitespace-nowrap'>
                {isActive ? 'EXPLORING COLLECTION' : 'VIEW COLLECTION'}
            </div>
        </div>
    )
}

const CategoryList = () => {
    const { categories } = useStoreContext()
    const mainCategories = categories.filter((c) => c.main)
    return (
        <div className='container-inline flex flex-row gap-x-4 p-4 overflow-x-auto snap-x snap-mandatory'>
            {mainCategories.map((category) => (
                <CategoryCard key={category.name} category={category} />
            ))}
        </div>
    )
}

const CategoryFilterItem = ({ category }: { category: CategoryType }) => {
    const { selectedCategory, selectCategory } = useStoreContext()
    const isSelected = selectedCategory == category
    return (
        <div
            className={twMerge('cursor-pointer hover:underline', isSelected && 'text-violet-500')}
            onClick={() => selectCategory(category.name)}
        >
            {category.name}
        </div>
    )
}

const CategoryFilterList = () => {
    const { categories } = useStoreContext()
    return (
        <div className=''>
            {categories.map((category) => (
                <CategoryFilterItem key={category.name} category={category} />
            ))}
        </div>
    )
}

export const Category = () => {}

Category.Card = CategoryCard
Category.List = CategoryList
Category.FilterList = CategoryFilterList
